import DeleteIcon from "@mui/icons-material/Delete";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  Button,
  Checkbox,
  Collapse,
  Grid,
  IconButton,
  Tooltip,
} from "@mui/material";
import { FormikErrors } from "formik";
import {
  FeedGroupInput,
  LookupLabwareType,
  Maybe,
  PlatesCreated,
} from "graphql/generated/schema-types";
import { DeepCopy } from "helpers/object-helpers";
import { ChangeEvent, Dispatch, SetStateAction, useEffect } from "react";
import { AssignedSystem } from "./AssignedSystem";
import { FeedGroupFormRowPlates } from "./FeedGroupFormRowPlates";
import { FeedGroupName } from "./FeedGroupName";
import { FeedMethod } from "./FeedMethod";
import { NumberOfPlates } from "./NumberOfPlates";
import { SelectStartTime } from "./SelectStartTime";

export interface GenericFieldProps {
  formikErrors?: string | undefined;
  handleBlur: (fieldOrEvent: any) => void;
  index: number;
  toggleDisabled: boolean;
}
export interface FeedGroupFormRowProps {
  asams: any;
  deleteRowHelper: (index: number) => void;
  errorMessageObj: (index: number) => any;
  formValues: any;
  feedGroupsToDelete: FeedGroupInput[];
  handleChange: (e: ChangeEvent<any>) => void;
  handleBlur: (fieldOrEvent: any) => void;
  index: number;
  setFeedGroupsToDelete: Dispatch<SetStateAction<FeedGroupInput[]>>;
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined,
  ) =>
    | Promise<void>
    | Promise<FormikErrors<{ feedGroupArray: FeedGroupInput[] }>>;
  toggleDisabled: boolean;
  isSelected: boolean;
  toggleSelected: (feedGroupId: number) => void;
  feedGroupCollapseOpen: number | null;
  setFeedGroupCollapseOpen: Dispatch<SetStateAction<number | null>>;
  plates: Maybe<
    {
      __typename?: "PlatesCreated";
    } & Pick<
      PlatesCreated,
      | "plateCreatedId"
      | "plateBarcode"
      | "comments"
      | "dateCreated"
      | "labwareTypeId"
    > & {
        labwareType?: Maybe<
          {
            __typename?: "LookupLabwareType";
          } & Pick<LookupLabwareType, "plateLayoutTypeId">
        >;
      }
  >[];
}

export const FeedGroupFormRow = ({
  asams,
  deleteRowHelper,
  errorMessageObj,
  feedGroupsToDelete,
  formValues,
  handleChange,
  handleBlur,
  index,
  setFeedGroupsToDelete,
  setFieldValue,
  toggleDisabled,
  isSelected,
  toggleSelected,
  feedGroupCollapseOpen,
  setFeedGroupCollapseOpen,
  plates,
}: FeedGroupFormRowProps) => {
  useEffect(() => {
    if (
      !plates ||
      plates.length === 0 ||
      !formValues.feedGroupArray[index].plateIds
    ) {
      return;
    }

    const availablePlateIds = new Set(
      plates.map((plate) => plate?.plateCreatedId),
    );
    const validPlateIds = formValues.feedGroupArray[index].plateIds.filter(
      (id: number) => availablePlateIds.has(id),
    );

    // If any plateIds were removed, update the field
    if (
      validPlateIds.length !== formValues.feedGroupArray[index].plateIds.length
    ) {
      setFieldValue(`feedGroupArray[${index}].plateIds`, validPlateIds);
      setFieldValue(
        `feedGroupArray[${index}].numberOfPlates`,
        validPlateIds.length,
      );
    }
  }, [plates, formValues.feedGroupArray[index].plateIds, index, setFieldValue]);

  const deleteHandler = (indexOfDeleted: number) => {
    const deletedFeedGroup = DeepCopy(
      formValues.feedGroupArray[indexOfDeleted],
    );
    if (deletedFeedGroup.feedGroupId !== null) {
      deletedFeedGroup.isActive = false;
      setFeedGroupsToDelete([...feedGroupsToDelete, deletedFeedGroup]);
    }
    deleteRowHelper(indexOfDeleted);
  };

  const feedGroupId = formValues.feedGroupArray[index].feedGroupId;
  const isUnsavedFeedGroup = typeof feedGroupId !== "number";

  const selectionTooltip = toggleDisabled
    ? "Feed task already exists for today and/or tomorrow"
    : isUnsavedFeedGroup
    ? "Save the feed group before creating a task for it"
    : "Include this feed group when creating feed tasks";

  return (
    <>
      <Grid container spacing={2} key={index}>
        <Grid item xs={0.5}>
          <Tooltip title={selectionTooltip}>
            <span>
              <Checkbox
                size="small"
                sx={{ mt: 0.8 }}
                checked={isSelected && !toggleDisabled}
                disabled={toggleDisabled || isUnsavedFeedGroup}
                onChange={() => toggleSelected(feedGroupId as number)}
                inputProps={{ "aria-label": "Include this feed group" }}
              />
            </span>
          </Tooltip>
        </Grid>

        <Grid item xs={0.5}>
          <Tooltip title="Add plates to feed group">
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={() =>
                setFeedGroupCollapseOpen((prev) =>
                  prev === index ? null : index,
                )
              }
              disabled={formValues.feedGroupArray[index].methodId === 0}
            >
              {feedGroupCollapseOpen === index ? (
                <KeyboardArrowUpIcon />
              ) : (
                <KeyboardArrowDownIcon />
              )}
            </IconButton>
          </Tooltip>
        </Grid>

        <Grid item xs={2}>
          <FeedGroupName
            formikErrors={errorMessageObj(index)?.feedGroupName}
            handleBlur={handleBlur}
            handleChange={handleChange}
            index={index}
            toggleDisabled={toggleDisabled}
            value={formValues.feedGroupArray[index].feedGroupName}
          />
        </Grid>

        <Grid item xs={2.75}>
          <FeedMethod
            formikErrors={errorMessageObj(index)?.methodId}
            handleBlur={handleBlur}
            index={index}
            setFieldValue={setFieldValue}
            toggleDisabled={toggleDisabled}
            value={formValues.feedGroupArray[index].methodId}
          />
        </Grid>

        <Grid item xs={2}>
          <AssignedSystem
            asams={asams}
            automationMethodId={
              formValues.feedGroupArray[index].automationMethodId
            }
            formikErrors={errorMessageObj(index)?.arraySystemId}
            handleBlur={handleBlur}
            index={index}
            setFieldValue={setFieldValue}
            toggleDisabled={toggleDisabled}
            value={formValues.feedGroupArray[index].arraySystemId}
          />
        </Grid>

        <Grid item xs={1.5}>
          <NumberOfPlates
            formikErrors={errorMessageObj(index)?.numberOfPlates}
            handleBlur={handleBlur}
            handleChange={handleChange}
            index={index}
            toggleDisabled={toggleDisabled}
            value={formValues.feedGroupArray[index].numberOfPlates}
          />
        </Grid>

        <Grid item xs={2}>
          <SelectStartTime
            formikErrors={errorMessageObj(index)?.startTimeScheduled}
            handleBlur={handleBlur}
            index={index}
            setFieldValue={setFieldValue}
            toggleDisabled={toggleDisabled}
            value={formValues.feedGroupArray[index].startTimeScheduled}
          />
        </Grid>
        <Grid item xs={0.25}>
          <Button
            disabled={toggleDisabled}
            onClick={() => {
              deleteHandler(index);
            }}
            sx={{ mt: 0.8 }}
          >
            <DeleteIcon color={toggleDisabled ? "disabled" : "primary"} />
          </Button>
        </Grid>
      </Grid>
      <Collapse
        sx={{ width: "100%", height: "100%" }}
        in={feedGroupCollapseOpen === index}
        timeout={"auto"}
        unmountOnExit
      >
        <FeedGroupFormRowPlates
          handleBlur={handleBlur}
          toggleDisabled={toggleDisabled}
          index={index}
          formikErrors={errorMessageObj(index)?.plateIds}
          method={formValues.feedGroupArray[index].methodId}
          value={formValues.feedGroupArray[index].plateIds}
          generateMystRack={formValues.feedGroupArray[index].generateMystRack}
          setFieldValue={setFieldValue}
          plates={plates}
        />
      </Collapse>
    </>
  );
};
